import React, { Fragment } from 'react';
import { animated, to, SpringValue } from '@react-spring/web';
import icon from '@images/wysiwyg_editor/drop_image_button_icon.svg';
import { isMobile } from 'react-device-detect';

type TProps = {
  clickedOnceAlready: boolean;
  location: {
    x: SpringValue<number>;
    width: SpringValue<number>;
    y: SpringValue<number>;
    height: SpringValue<number>;
  };
  showBorder: boolean;
  stackOrder: number;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
};

const texts = {
  desktop: {
    first: 'Click here to add an image',
    subsequent: 'Click here again to add an image'
  },
  mobile: {
    first: 'Tap here to add an image',
    subsequent: 'Tap here again to add an image'
  }
};

export default function AddImageButton({
  clickedOnceAlready,
  location,
  showBorder,
  stackOrder,
  onMouseEnter,
  onMouseLeave
}: TProps): React.ReactElement {
  const textClickNumberKey = clickedOnceAlready ? 'subsequent' : 'first';
  const text = isMobile
    ? texts.mobile[textClickNumberKey]
    : texts.desktop[textClickNumberKey];

  return (
    <animated.button
      style={{
        alignItems: 'center',
        background: '#fbfcfe',
        border: showBorder ? '1px dashed #A5C7E2' : '',
        color: '#87929D',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Assistant, Arial, sans-serif',
        fontSize: '1.4rem',
        fontWeight: 400,
        height: location.height,
        justifyContent: 'center',
        left: location.x,
        lineHeight: '1.5',
        outline: 'none',
        overflow: 'hidden',
        padding: '0.5rem 0.2rem',
        position: 'absolute',
        top: location.y,
        width: location.width,
        zIndex: stackOrder
      }}
      type="button"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Fragment>
        <animated.img
          src={icon as string}
          alt=""
          style={{
            marginBottom: to(
              [location.height, location.width],
              (height, width) => {
                return height * width >= 5000 && height > 125 ? 24 : 0;
              }
            ),
            marginTop: to(
              [location.height, location.width],
              (height, width) => {
                return height * width >= 5000 && height > 125 ? 5 : 0;
              }
            ),
            maxHeight: to([location.height], (height) => height - 4),
            display: to([location.height, location.width], (height, width) => {
              return height * width < 5000 || height > 125 ? 'block' : 'none';
            })
          }}
        />
        <animated.span
          style={{
            margin: '0 5px',
            maxWidth: 110,
            display: to([location.height, location.width], (height, width) => {
              return height * width >= 5000 || height > 125 ? 'block' : 'none';
            })
          }}
        >
          {text}
        </animated.span>
      </Fragment>
    </animated.button>
  );
}
